import { lazy, Suspense, useEffect, useState } from 'react'
import { haalSessie, opUitgelogd } from './lib/api'
import { usePad } from './lib/router'
import { laadIk } from './lib/staat'
import { FoutVak, Laden, Meldingen } from './onderdelen/ui'
import Klaar from './onderdelen/Klaar'
import Boekingen from './schermen/Boekingen'
import Factuur from './schermen/Factuur'
import Geschiedenis from './schermen/Geschiedenis'
import Poort from './schermen/Poort'
import Scannen from './schermen/Scannen'
import Start from './schermen/Start'
import Tijdlijn from './schermen/Tijdlijn'
import Todo from './schermen/Todo'
import Voorkeuren from './schermen/Voorkeuren'
import Zoeken, { Resultaten } from './schermen/Zoeken'

/* Originelen haalt pdf.js binnen; dat hoort niet in de eerste lading. */
const Originelen = lazy(() => import('./schermen/Originelen'))

/* ===========================================================================
 *  De app
 *
 *  Eerst: ben je binnen? Zo niet, de poort (inloggen via het dashboard). Zo
 *  wel: wie ben je en wat mag je (actie `ik`), en dan het scherm dat bij het
 *  adres hoort.
 * =========================================================================== */

type Stand = { soort: 'laden' } | { soort: 'buiten'; fout: string | null } | { soort: 'binnen' } | { soort: 'stuk'; fout: string }

export default function App() {
  const [stand, setStand] = useState<Stand>({ soort: 'laden' })

  useEffect(() => {
    opUitgelogd(() => setStand({ soort: 'buiten', fout: null }))
    const fout = new URLSearchParams(location.search).get('fout')
    if (fout) {
      /* De melding één keer tonen, en niet in het adres laten staan. */
      history.replaceState(history.state, '', location.pathname)
    }
    ;(async () => {
      const sessie = await haalSessie()
      if (!sessie) { setStand({ soort: 'buiten', fout }); return }
      try {
        await laadIk()
        setStand({ soort: 'binnen' })
      } catch (e) {
        setStand(e instanceof Error && /uitgelogd/i.test(e.message)
          ? { soort: 'buiten', fout: null }
          : { soort: 'stuk', fout: e instanceof Error ? e.message : String(e) })
      }
    })()
  }, [])

  if (stand.soort === 'laden') return <div className="scherm"><Laden tekst="TW10 wordt geopend…" /></div>
  if (stand.soort === 'buiten') return <Poort fout={stand.fout} />
  if (stand.soort === 'stuk') {
    return (
      <div className="scherm">
        <FoutVak tekst={stand.fout} opnieuw={() => location.reload()} />
      </div>
    )
  }
  return (
    <>
      <Scherm />
      <Meldingen />
      {/* Het feest als er niets meer op je wacht, zoals in TW10 Online. */}
      <Klaar />
    </>
  )
}

function Scherm() {
  const pad = usePad()

  const factuur = /^\/factuur\/([^/]+)(?:\/(originelen|boekingen|tijdlijn))?\/?$/.exec(pad)
  if (factuur) {
    const id = decodeURIComponent(factuur[1] ?? '')
    if (factuur[2] === 'originelen') return <Suspense fallback={<Laden />}><Originelen id={id} /></Suspense>
    if (factuur[2] === 'boekingen') return <Boekingen id={id} />
    if (factuur[2] === 'tijdlijn') return <Tijdlijn id={id} />
    return <Factuur id={id} />
  }

  switch (pad.replace(/\/$/, '') || '/') {
    case '/todo': return <Todo />
    case '/zoeken': return <Zoeken />
    case '/zoeken/resultaten': return <Resultaten />
    case '/scannen': return <Scannen />
    case '/voorkeuren': return <Voorkeuren />
    case '/geschiedenis': return <Geschiedenis />
    default: return <Start />
  }
}
